import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Mail, RefreshCw, Lock, Search, Receipt, X, ExternalLink } from 'lucide-react';
import ProtectedRoute from '../components/ProtectedRoute.jsx';
import { useAuth } from '../contexts/AuthContext';
import { billingAPI } from '../services/api.jsx';
import './Billing.css';

const PAGE_SIZE = 50;
const EMAIL_SIZE_LIMIT_BYTES = 145 * 1024 * 1024;

const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
};

// Invoice dates are calendar dates (YYYY-MM-DD); parse as local to avoid a timezone day shift.
const formatDate = (value) => {
  if (!value) return '-';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const [, y, m, d] = match.map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
};

const saveBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const fileNameFromDisposition = (header, fallback) => {
  const value = String(header || '');
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(value);
  if (utf8) {
    try { return decodeURIComponent(utf8[1]); } catch (error) { /* fall through */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(value);
  return plain ? plain[1] : fallback;
};

const readErrorMessage = async (error, fallback) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.error || fallback;
    } catch (parseError) {
      return fallback;
    }
  }
  return data?.error || fallback;
};

const AccessNotice = ({ access, message, folderUrl, onSignOut, onRetry }) => (
  <div className="card billing-access-notice">
    <Lock size={40} className="billing-access-icon" />
    {access === 'scope_missing' ? (
      <>
        <h2>One more step to connect SharePoint</h2>
        <p>
          The Billing tool needs permission to read SharePoint on your behalf. Sign out and sign back in to
          grant it, then return to this page.
        </p>
        <div className="billing-access-actions">
          <button type="button" className="btn btn-primary" onClick={onSignOut}>Sign out and sign back in</button>
        </div>
      </>
    ) : (
      <>
        <h2>You don&apos;t have access to the invoice folder yet</h2>
        <p>
          Invoices are stored in the Finance SharePoint site
          (<strong>Finance Folder › AR-Accounts Receivable</strong>), and your Microsoft 365 account doesn&apos;t
          currently have permission to view it.
        </p>
        <p>
          Open the folder in SharePoint and choose <strong>Request access</strong>, or contact the Finance site
          owner. Once access is granted, come back and select <strong>Try again</strong>.
        </p>
        {message && <p className="billing-access-detail">{message}</p>}
        <div className="billing-access-actions">
          <a className="btn btn-primary" href={folderUrl} target="_blank" rel="noopener noreferrer">
            Request access <ExternalLink size={14} />
          </a>
          <button type="button" className="btn btn-secondary" onClick={onRetry}>Try again</button>
        </div>
      </>
    )}
  </div>
);

const EmailModal = ({ invoices, onClose, onSend, sending }) => {
  const clients = [...new Set(invoices.map(item => item.client))];
  const totalSize = invoices.reduce((sum, item) => sum + item.size, 0);
  const [form, setForm] = useState({
    to: '',
    cc: '',
    subject: `Invoices${clients.length === 1 ? ` - ${clients[0]}` : ''}`,
    message: ''
  });
  const tooLarge = totalSize > EMAIL_SIZE_LIMIT_BYTES;

  const update = (field) => (event) => setForm(prev => ({ ...prev, [field]: event.target.value }));

  const submit = (event) => {
    event.preventDefault();
    onSend(form);
  };

  return (
    <div className="billing-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="billing-email-title">
      <form className="billing-modal card" onSubmit={submit}>
        <div className="billing-modal-header">
          <h2 id="billing-email-title">Email invoices</h2>
          <button type="button" className="billing-icon-button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <p className="billing-muted">
          {invoices.length} invoice{invoices.length === 1 ? '' : 's'} ({formatBytes(totalSize)}) will be zipped and sent
          from your mailbox.
        </p>
        {tooLarge && (
          <div className="error">The selection is too large to email. Download the zip instead or select fewer invoices.</div>
        )}
        <label>
          To <span className="billing-muted">(separate multiple addresses with commas)</span>
          <input type="text" value={form.to} onChange={update('to')} required placeholder="name@example.com" />
        </label>
        <label>
          CC
          <input type="text" value={form.cc} onChange={update('cc')} />
        </label>
        <label>
          Subject
          <input type="text" value={form.subject} onChange={update('subject')} maxLength={255} />
        </label>
        <label>
          Message
          <textarea rows={5} value={form.message} onChange={update('message')} />
        </label>
        <div className="billing-modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={sending || tooLarge}>
            {sending ? 'Sending…' : 'Send email'}
          </button>
        </div>
      </form>
    </div>
  );
};

const BillingContent = () => {
  const { hasPermission, logout } = useAuth();
  const navigate = useNavigate();
  const canEmail = hasPermission('billing:email');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);

  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [clientQuery, setClientQuery] = useState('');
  const [clientPanelOpen, setClientPanelOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [completedQuery, setCompletedQuery] = useState('');
  const [sort, setSort] = useState({ key: 'invoiceDate', dir: 'desc' });
  const [page, setPage] = useState(1);

  const [selection, setSelection] = useState(() => new Map());
  const selectedIds = new Set(selection.keys());
  const snapshotRef = useRef(undefined);
  const [reloadKey, setReloadKey] = useState(0);
  const [jobPending, setJobPending] = useState(false);
  const [indexAction, setIndexAction] = useState(null);
  const [indexActionPending, setIndexActionPending] = useState(false);
  const [indexActionError, setIndexActionError] = useState('');
  const indexControlGeneration = useRef(0);
  const [indexFolders, setIndexFolders] = useState(null);
  const [folderIds, setFolderIds] = useState(null);
  const [folderLoading, setFolderLoading] = useState(false);
  const [folderError, setFolderError] = useState('');
  const savedFolderIds = JSON.stringify(data?.sync?.selectedFolders?.map(folder => folder.id) || null);
  useEffect(() => {
    setFolderIds(JSON.parse(savedFolderIds));
  }, [savedFolderIds]);
  const [selecting, setSelecting] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const query = useMemo(() => ({
    years: JSON.stringify(selectedYears), clients: JSON.stringify(selectedClients),
    dateFrom, dateTo, search: appliedSearch, sort: sort.key, dir: sort.dir, page, pageSize: PAGE_SIZE
  }), [selectedYears, selectedClients, dateFrom, dateTo, appliedSearch, sort, page]);
  const queryRef = useRef(query);
  queryRef.current = query;

  const loadInvoices = useCallback(() => setReloadKey(value => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setRefreshing(true);
    const timer = setTimeout(async () => {
      try {
        const generation = indexControlGeneration.current;
        const response = await billingAPI.getInvoices(query, { signal: controller.signal });
        if (!active || generation !== indexControlGeneration.current) return;
        if (snapshotRef.current !== undefined && snapshotRef.current !== response.data.snapshotId) {
          setSelection(new Map());
          setEmailOpen(false);
          setNotice({ type: 'success', text: 'The invoice index was updated. Selection has been cleared; please select from the current list.' });
        }
        snapshotRef.current = response.data.snapshotId;
        setData(response.data);
        setCompletedQuery(JSON.stringify(query));
        setError('');
      } catch (err) {
        if (active && !controller.signal.aborted) setError(err?.response?.data?.error || 'Failed to query the invoice index.');
      } finally {
        if (active) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    }, 250);
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [query, reloadKey]);

  const syncing = ['queued', 'running'].includes(data?.sync?.status);
  const retryScheduled = data?.sync?.status === 'failed' && Boolean(data?.sync?.nextRunAt);
  useEffect(() => {
    if (!syncing && !retryScheduled) return undefined;
    let active = true;
    let timer;
    const poll = async () => {
      try {
        const generation = indexControlGeneration.current;
        const response = await billingAPI.getStatus();
        if (!active || generation !== indexControlGeneration.current) return;
        const status = response.data;
        setData(previous => previous ? { ...previous, sync: status.sync, capabilities: status.capabilities } : previous);
        if (!['queued', 'running'].includes(status.sync.status)
          && (syncing || status.snapshotId !== snapshotRef.current)) loadInvoices();
      } catch (err) {
        if (active) setError(err?.response?.data?.error || 'Unable to check indexing progress.');
      }
      if (active) timer = setTimeout(poll, syncing ? 3000 : 30000);
    };
    timer = setTimeout(poll, 1000);
    return () => { active = false; clearTimeout(timer); };
  }, [syncing, retryScheduled, loadInvoices, reloadKey]);

  const runJob = async (connect = false) => {
    setJobPending(true);
    try {
      const options = indexFolders === null ? {} : { folderIds };
      const response = await (connect ? billingAPI.connect(options) : billingAPI.sync(options));
      setData(previous => ({ ...previous, sync: response.data.sync }));
      setError('');
      loadInvoices();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not start invoice indexing.');
    } finally {
      setJobPending(false);
    }
  };

  const confirmIndexAction = async () => {
    setIndexActionPending(true);
    setIndexActionError('');
    indexControlGeneration.current += 1;
    try {
      const clear = indexAction === 'clear';
      const response = await (clear ? billingAPI.clearIndex() : billingAPI.stopIndexing());
      indexControlGeneration.current += 1;
      const result = response.data;
      setData(previous => ({
        ...previous, sync: result.sync, snapshotId: result.snapshotId, lastSyncedAt: result.lastSyncedAt,
        ...(clear ? { invoices: [], total: 0, filteredTotal: 0, filteredClientCount: 0, years: [], yearCounts: {}, clients: [], searchSummary: null } : {})
      }));
      if (clear) {
        setSelection(new Map());
        setEmailOpen(false);
        setPage(1);
        snapshotRef.current = null;
      }
      setNotice({
        type: 'success',
        text: clear
          ? 'Local invoice metadata cleared. SharePoint files were not changed. Automatic indexing is paused until you re-index.'
          : 'Indexing stopped. The last completed index and SharePoint files are unchanged. Automatic indexing is paused until you re-index.'
      });
      setError('');
      setIndexAction(null);
      loadInvoices();
    } catch (err) {
      setIndexActionError(err?.response?.data?.error || 'The indexing action failed. Try again.');
      loadInvoices();
    } finally {
      setIndexActionPending(false);
    }
  };

  const loadIndexFolders = async () => {
    setFolderLoading(true);
    setFolderError('');
    try {
      const response = await billingAPI.getIndexFolders();
      setIndexFolders(response.data.folders);
    } catch (err) {
      setFolderError(err?.response?.data?.error || 'Failed to load indexing folders.');
    } finally {
      setFolderLoading(false);
    }
  };
  const invalidFolderSelection = indexFolders !== null && folderIds !== null
    && (!folderIds.length || folderIds.some(id => !indexFolders.some(folder => folder.id === id)));

  const pageItems = data?.invoices || [];
  const filteredTotal = data?.filteredTotal || 0;

  useEffect(() => {
    setPage(1);
  }, [selectedYears, selectedClients, dateFrom, dateTo, appliedSearch, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredTotal / PAGE_SIZE));
  useEffect(() => {
    if (!refreshing && page > totalPages) setPage(totalPages);
  }, [refreshing, page, totalPages]);

  const selectedInvoices = [...selection.values()];
  const selectedSize = selectedInvoices.reduce((sum, item) => sum + item.size, 0);

  const visibleClients = (data?.clients || []).filter(client =>
    client.toLowerCase().includes(clientQuery.trim().toLowerCase()));

  const toggleYear = (year) => setSelectedYears(prev =>
    (prev.includes(year) ? prev.filter(item => item !== year) : [...prev, year]));

  const toggleClient = (client) => setSelectedClients(prev =>
    (prev.includes(client) ? prev.filter(item => item !== client) : [...prev, client]));

  const clearFilters = () => {
    setSelectedYears([]);
    setSelectedClients([]);
    setDateFrom('');
    setDateTo('');
    setSearch('');
    setAppliedSearch('');
    setClientQuery('');
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setRefreshing(true);
    setError('');
    setPage(1);
    setAppliedSearch(search.trim());
    loadInvoices();
  };

  const toggleSelected = (id) => {
    setSelection(prev => {
      const next = new Map(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        const invoice = pageItems.find(item => item.id === id);
        if (invoice) next.set(id, invoice);
      }
      return next;
    });
  };

  const addToSelection = (items) => {
    setSelection(prev => {
      const next = new Map(prev);
      for (const item of items) {
        if (next.has(item.id)) continue;
        next.set(item.id, item);
      }
      return next;
    });
  };

  const selectFiltered = async () => {
    setSelecting(true);
    const snapshot = data.snapshotId;
    const generation = indexControlGeneration.current;
    const selectionQuery = query;
    try {
      const items = new Map();
      let nextPage = 1;
      let matchingTotal;
      do {
        const response = await billingAPI.getInvoices({ ...query, page: nextPage, pageSize: 100 });
        if (queryRef.current !== selectionQuery) {
          setNotice({ type: 'error', text: 'Filters changed while selecting invoices. Select all filtered again.' });
          return;
        }
        if (response.data.snapshotId !== snapshot || snapshotRef.current !== snapshot || generation !== indexControlGeneration.current) {
          setNotice({ type: 'error', text: 'The index changed. Reload the list and select invoices again.' });
          loadInvoices();
          return;
        }
        if (matchingTotal === undefined) matchingTotal = response.data.filteredTotal;
        if (response.data.filteredTotal !== matchingTotal || (matchingTotal > items.size && !response.data.invoices.length)) {
          throw new Error('The matching invoice list changed. Reload and select again.');
        }
        response.data.invoices.forEach(item => items.set(item.id, item));
        nextPage += 1;
      } while ((nextPage - 1) * 100 < matchingTotal);
      if (items.size !== matchingTotal) {
        throw new Error('The matching invoice list changed. Reload and select again.');
      }
      addToSelection([...items.values()]);
    } catch (err) {
      setNotice({ type: 'error', text: err?.response?.data?.error || err.message || 'Failed to select matching invoices.' });
    } finally {
      setSelecting(false);
    }
  };

  const allPageSelected = pageItems.length > 0 && pageItems.every(item => selectedIds.has(item.id));

  const togglePageSelection = () => {
    if (allPageSelected) {
      setSelection(prev => {
        const next = new Map(prev);
        pageItems.forEach(item => next.delete(item.id));
        return next;
      });
    } else {
      addToSelection(pageItems);
    }
  };

  const changeSort = (key) => {
    setSort(prev => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'invoiceDate' || key === 'year' ? 'desc' : 'asc' }));
  };

  const sortIndicator = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  const downloadZip = async () => {
    if (selectedInvoices.length === 0) return;
    try {
      setZipping(true);
      setNotice(null);
      const response = await billingAPI.downloadZip(selectedInvoices.map(item => item.id), data.snapshotId);
      const fallbackName = `invoices-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.zip`;
      saveBlob(response.data, fileNameFromDisposition(response.headers?.['content-disposition'], fallbackName));
      setNotice({ type: 'success', text: `Downloaded ${selectedInvoices.length} invoice${selectedInvoices.length === 1 ? '' : 's'} as a zip.` });
    } catch (err) {
      setNotice({ type: 'error', text: await readErrorMessage(err, 'Failed to create the zip file.') });
    } finally {
      setZipping(false);
    }
  };

  const downloadSingle = async (invoice) => {
    try {
      const response = await billingAPI.downloadInvoice(invoice.id, data.snapshotId);
      saveBlob(response.data, invoice.name);
    } catch (err) {
      setNotice({ type: 'error', text: await readErrorMessage(err, `Failed to download ${invoice.name}.`) });
    }
  };

  const sendEmail = async (form) => {
    try {
      setSending(true);
      const response = await billingAPI.emailZip({
        itemIds: selectedInvoices.map(item => item.id),
        snapshotId: data.snapshotId,
        to: form.to,
        cc: form.cc,
        subject: form.subject,
        message: form.message
      });
      setEmailOpen(false);
      setNotice({
        type: 'success',
        text: `Emailed ${response.data.invoiceCount} invoice${response.data.invoiceCount === 1 ? '' : 's'} (${formatBytes(response.data.zipBytes)} zip).`
      });
    } catch (err) {
      setNotice({ type: 'error', text: await readErrorMessage(err, 'Failed to email invoices.') });
    } finally {
      setSending(false);
    }
  };

  const signOutForConsent = () => {
    sessionStorage.setItem('postLoginRedirect', '/billing');
    logout();
    navigate('/login');
  };

  if (loading && !data) {
    return <div className="loading">Loading invoice index…</div>;
  }

  if (error && !data) {
    return (
      <div className="container billing-page">
        <h1>Billing</h1>
        <div className="error">{error}</div>
        <button type="button" className="btn btn-primary" onClick={() => loadInvoices(true)}>Try again</button>
      </div>
    );
  }

  if (data && data.access !== 'granted') {
    return (
      <div className="container billing-page">
        <h1>Billing</h1>
        <AccessNotice
          access={data.access}
          message={data.message}
          folderUrl={data.folderUrl}
          onSignOut={signOutForConsent}
          onRetry={() => loadInvoices(true)}
        />
      </div>
    );
  }

  const hasFilters = selectedYears.length || selectedClients.length || dateFrom || dateTo || appliedSearch || search;
  const searchChanged = search.trim() !== appliedSearch;
  const resultsCurrent = completedQuery === JSON.stringify(query) && !refreshing && !error;

  return (
    <div className="container billing-page">
      <div className="billing-header">
        <div>
          <h1><Receipt size={28} /> Billing</h1>
          <p className="billing-muted">
            Indexed AR invoices from SharePoint, organized by year and client.{' '}
            <a href={data.folderUrl} target="_blank" rel="noopener noreferrer">Open folder <ExternalLink size={12} /></a>
          </p>
        </div>
        <div className="billing-action-buttons">
          <button type="button" className="btn btn-secondary billing-refresh" onClick={loadInvoices} disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? 'billing-spin' : ''} /> {refreshing ? 'Loading…' : 'Reload list'}
          </button>
        </div>
      </div>

      {error && <div className="error" role="alert">{error}</div>}
      <div className={`card billing-index-status ${data.sync?.stale ? 'billing-index-stale' : ''}`} role="status">
        <strong>{syncing ? 'Updating invoice metadata in the background' : data.sync?.status === 'stopped' ? 'Indexing stopped' : data.sync?.status === 'cleared' ? 'Local invoice index cleared' : data.sync?.status === 'setup_required' ? 'Invoice index not connected' : data.sync?.stale ? 'Invoice index needs updating' : 'Invoice index ready'}</strong>
        <p>
          {data.lastSyncedAt ? `Last successful update: ${new Date(data.lastSyncedAt).toLocaleString()}. ` : 'No successful index is available yet. '}
          {syncing ? `${data.sync.progress || 0} invoices indexed so far. ` : ['stopped', 'cleared'].includes(data.sync?.status) ? 'Automatic indexing is paused. Use Re-index now to rebuild/resume daily indexing. SharePoint files are unchanged. ' : 'Metadata refreshes once daily. '}
          {!data.snapshotId && !['stopped', 'cleared'].includes(data.sync?.status) && 'A superuser must connect the indexing account and complete the initial scan.'}
        </p>
        {data.sync?.lastError && <p className="error">{data.sync.lastError}</p>}
        {data.sync?.nextRunAt && data.sync?.status !== 'running' && (
          <p>{data.sync.status === 'failed' || (data.sync.status === 'queued' && data.sync.throttleUntil)
            ? 'Automatic retry scheduled for: ' : 'Next scheduled update: '}
          {new Date(data.sync.nextRunAt).toLocaleString()}
          {data.sync.retryCount > 0 ? ` (${data.sync.retryCount} consecutive failed attempt${data.sync.retryCount === 1 ? '' : 's'})` : ''}.</p>
        )}
        <p>Indexing scope: {data.sync?.selectedFolders
          ? data.sync.selectedFolders.map(folder => folder.name).join(', ')
          : 'All top-level year folders'}.</p>
        <p className="billing-muted">{data.sync?.syncMode === 'delta'
          ? 'Updates use SharePoint change tracking instead of re-reading every folder.'
          : 'The first update builds an Accounts Receivable metadata baseline; later updates request only changes.'}{' '}
          The invoice count is progress, not a percentage. Updates become visible only after the entire job succeeds. Previously indexed, unselected year folders remain available but are not refreshed.</p>
        <p className="billing-muted">Billing users share this metadata list. Your own SharePoint permissions control opening, downloading, and emailing selected files.</p>
      </div>

      {(data.capabilities?.canSync || data.capabilities?.canConnect) && (
        <details className="card billing-index-status billing-admin-panel">
          <summary>Admin indexing controls</summary>
          <div className="billing-admin-content">
            <div className="billing-action-buttons">
              {data.capabilities?.canSync && (
                <>
                  <button type="button" className="btn btn-primary" onClick={() => runJob()} disabled={jobPending || indexActionPending || syncing || folderLoading || invalidFolderSelection || data.sync?.status === 'setup_required'}>Re-index now</button>
                  <button type="button" className="btn btn-secondary" onClick={() => { setIndexActionError(''); setIndexAction('stop'); }} disabled={jobPending || indexActionPending || data.sync?.status === 'setup_required' || ['stopped', 'cleared'].includes(data.sync?.status)}>Stop indexing</button>
                  <button type="button" className="btn btn-secondary" onClick={() => { setIndexActionError(''); setIndexAction('clear'); }} disabled={jobPending || indexActionPending || data.sync?.status === 'setup_required' || data.sync?.status === 'cleared'}>Clear index</button>
                </>
              )}
              {data.capabilities?.canConnect && (
                <button type="button" className="btn btn-secondary" onClick={() => runJob(true)} disabled={jobPending || indexActionPending || syncing || folderLoading || invalidFolderSelection}>
                  {data.sync?.status === 'setup_required' ? 'Connect indexing account' : 'Reconnect indexing account'}
                </button>
              )}
            </div>
            {data.capabilities?.canSync && (
              <>
                <h2>Folders to index</h2>
                <p>Choose top-level year folders, such as 2025 AR and 2026 AR. Client subfolders are included automatically. These settings apply to the shared daily job, not just your view.</p>
                <button type="button" className="btn btn-secondary" onClick={loadIndexFolders} disabled={folderLoading || jobPending || syncing}>
                  {folderLoading ? 'Loading folders…' : indexFolders === null ? 'Choose indexing folders' : 'Reload folder choices'}
                </button>
                {folderError && <p className="error" role="alert">{folderError}</p>}
                {indexFolders !== null && (
                  <fieldset disabled={syncing || jobPending || folderLoading} className="billing-index-folders">
                    <legend>Indexing folders</legend>
                    <label className="billing-checkbox-row">
                      <input type="checkbox" checked={folderIds === null} onChange={event => setFolderIds(event.target.checked ? null : [])} />
                      All year folders (including new year folders added later)
                    </label>
                    {indexFolders.map(folder => (
                      <label key={folder.id} className="billing-checkbox-row">
                        <input type="checkbox" checked={folderIds === null || folderIds.includes(folder.id)} onChange={event => {
                          const ids = folderIds === null ? indexFolders.map(item => item.id) : folderIds;
                          setFolderIds(event.target.checked ? [...new Set([...ids, folder.id])] : ids.filter(id => id !== folder.id));
                        }} />
                        {folder.name}
                      </label>
                    ))}
                    {indexFolders.length === 0 && <p>No top-level year folders were found. Verify the SharePoint folder configuration.</p>}
                    {invalidFolderSelection && <p className="error">Select at least one available year folder or choose all year folders. Previously selected folders may have been removed.</p>}
                    <p>Use <strong>{data.sync?.status === 'setup_required' ? 'Connect indexing account' : 'Re-index now'}</strong> to save this selection and start indexing. Changes are not saved until then. Previously indexed folders outside this selection stay in the shared list but are not refreshed. Choose all year folders to reconcile the entire list.</p>
                  </fieldset>
                )}
              </>
            )}
          </div>
        </details>
      )}

      <div className="billing-stats">
        <div className="card billing-stat">
          <span className="billing-stat-value">{filteredTotal.toLocaleString()}</span>
          <span className="billing-stat-label">{hasFilters ? `of ${(data.total || 0).toLocaleString()} invoices` : 'Invoices'}</span>
        </div>
        <div className="card billing-stat">
          <span className="billing-stat-value">{(data.filteredClientCount || 0).toLocaleString()}</span>
          <span className="billing-stat-label">Clients</span>
        </div>
        <div className="card billing-stat">
          <span className="billing-stat-value">{(data.years || []).length}</span>
          <span className="billing-stat-label">Years</span>
        </div>
        <div className="card billing-stat">
          <span className="billing-stat-value">{selectedIds.size}</span>
          <span className="billing-stat-label">Selected ({formatBytes(selectedSize)})</span>
        </div>
      </div>

      <div className="card billing-filters">
        <div className="billing-filter-row">
          <form className={`billing-search-form ${searchChanged ? 'billing-search-pending' : ''}`} onSubmit={submitSearch} role="search">
            <div className="billing-search">
              <Search size={16} />
              <input
                type="search"
                placeholder="Search invoices; separate numbers with , or ;"
                title="Search by file name, client, or folder. Separate up to 100 entries with commas or semicolons to match any (200 words total)."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                aria-label="Search invoices"
                aria-describedby="billing-search-help"
              />
            </div>
            <button type="submit" className="btn btn-primary billing-search-submit" disabled={refreshing && !searchChanged}>
              {refreshing ? <RefreshCw size={16} className="billing-spin" /> : <Search size={16} />}
              {refreshing && !searchChanged ? 'Searching...' : 'Search'}
            </button>
          </form>
          <label className="billing-date">
            From
            <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label className="billing-date">
            To
            <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
          <div className="billing-client-filter">
            <button type="button" className="btn btn-secondary" onClick={() => setClientPanelOpen(open => !open)} aria-expanded={clientPanelOpen}>
              Subfolders{selectedClients.length ? ` (${selectedClients.length})` : ''} ▾
            </button>
            {clientPanelOpen && (
              <div className="billing-client-panel card">
                <input
                  type="search"
                  placeholder="Find a subfolder..."
                  value={clientQuery}
                  onChange={(event) => setClientQuery(event.target.value)}
                  autoFocus
                />
                <div className="billing-client-list">
                  {visibleClients.length === 0 && <div className="billing-muted">No matching clients</div>}
                  {visibleClients.map(client => (
                    <label key={client} className="billing-checkbox-row">
                      <input type="checkbox" checked={selectedClients.includes(client)} onChange={() => toggleClient(client)} />
                      {client}
                    </label>
                  ))}
                </div>
                <div className="billing-client-panel-actions">
                  <button type="button" className="billing-link-button" onClick={() => setSelectedClients([])}>Clear</button>
                  <button type="button" className="billing-link-button" onClick={() => setClientPanelOpen(false)}>Done</button>
                </div>
              </div>
            )}
          </div>
          {hasFilters ? (
            <button type="button" className="billing-link-button" onClick={clearFilters}>Clear filters</button>
          ) : null}
        </div>

        <div id="billing-search-help" className="billing-search-feedback" role="status" aria-live="polite">
          {refreshing ? (
            <><RefreshCw size={16} className="billing-spin" /> Searching the invoice index. Please wait...</>
          ) : searchChanged ? (
            <strong>Search changed. Select Search or press Enter to apply. Results below have not been updated.</strong>
          ) : appliedSearch ? (
            resultsCurrent ? `Search complete: ${filteredTotal.toLocaleString()} matching invoices.` : 'Search did not complete. Retry with Search.'
          ) : 'Enter invoice numbers separated by commas or semicolons, then select Search or press Enter.'}
        </div>

        {appliedSearch && !searchChanged && resultsCurrent && data.searchSummary && (
          <section className="billing-search-summary" aria-label="Invoice search summary" role="status">
            <strong>{data.searchSummary.foundCount} found / {data.searchSummary.notFoundCount} not found</strong>
            <p>Of {data.searchSummary.requestedCount} unique search entries, under the current year, client, and date filters. Counts cover all result pages, not just this page.</p>
            {data.searchSummary.notFound.length > 0 && (
              <div>
                <strong>Not found:</strong>
                <ul className="billing-missing-invoices">
                  {data.searchSummary.notFound.map(entry => <li key={entry}>{entry}</li>)}
                </ul>
              </div>
            )}
          </section>
        )}

        <div className="billing-year-chips" aria-label="Filter by year">
          {(data.years || []).map(year => (
            <button
              type="button"
              key={year}
              className={`billing-chip ${selectedYears.includes(year) ? 'active' : ''}`}
              onClick={() => toggleYear(year)}
              aria-pressed={selectedYears.includes(year)}
            >
              {year} <span className="billing-chip-count">{data.yearCounts?.[year] || 0}</span>
            </button>
          ))}
        </div>

        {selectedClients.length > 0 && (
          <div className="billing-year-chips">
            {selectedClients.map(client => (
              <button type="button" key={client} className="billing-chip active" onClick={() => toggleClient(client)}>
                {client} <X size={12} />
              </button>
            ))}
          </div>
        )}
      </div>

      {notice && (
        <div className={notice.type === 'error' ? 'error' : 'success'} role="status">
          {notice.text}
          <button type="button" className="billing-icon-button" onClick={() => setNotice(null)} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}

      <div className="billing-actions">
        <span className="billing-muted">
          {selectedIds.size} selected
          {selectedIds.size > 0 && (
            <> · <button type="button" className="billing-link-button" onClick={() => setSelection(new Map())}>Clear selection</button></>
          )}
          {filteredTotal > 0 && (
            <> · <button type="button" className="billing-link-button" onClick={selectFiltered} disabled={selecting || refreshing}>
              {selecting ? 'Selecting…' : 'Select all filtered'}
            </button></>
          )}
        </span>
        <div className="billing-action-buttons">
          <button type="button" className="btn btn-primary" onClick={downloadZip} disabled={selectedIds.size === 0 || zipping}>
            <Download size={16} /> {zipping ? 'Preparing zip…' : 'Download zip'}
          </button>
          {canEmail && (
            <button type="button" className="btn btn-primary" onClick={() => setEmailOpen(true)} disabled={selectedIds.size === 0}>
              <Mail size={16} /> Email zip
            </button>
          )}
        </div>
      </div>

      <div className="card billing-table-card" aria-busy={refreshing}>
        {pageItems.length === 0 ? (
          <div className="billing-empty">
            {data.sync?.status === 'cleared' ? 'Local metadata has been cleared. SharePoint files are unchanged. Re-index to repopulate the list.' : !data.snapshotId ? 'Invoice metadata will appear after the first successful background scan.' : data.total === 0 ? 'No invoices were found in the SharePoint folder.' : 'No invoices match the current filters.'}
          </div>
        ) : (
          <table className="billing-table">
            <thead>
              <tr>
                <th className="billing-col-check">
                  <input type="checkbox" checked={allPageSelected} onChange={togglePageSelection} aria-label="Select all on this page" />
                </th>
                <th><button type="button" onClick={() => changeSort('name')}>Invoice{sortIndicator('name')}</button></th>
                <th><button type="button" onClick={() => changeSort('client')}>Subfolder{sortIndicator('client')}</button></th>
                <th><button type="button" onClick={() => changeSort('year')}>Year{sortIndicator('year')}</button></th>
                <th><button type="button" onClick={() => changeSort('invoiceDate')}>Date{sortIndicator('invoiceDate')}</button></th>
                <th><button type="button" onClick={() => changeSort('size')}>Size{sortIndicator('size')}</button></th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {pageItems.map(item => (
                <tr key={item.id} className={selectedIds.has(item.id) ? 'selected' : ''}>
                  <td className="billing-col-check">
                    <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleSelected(item.id)} aria-label={`Select ${item.name}`} />
                  </td>
                  <td>
                    <a href={item.webUrl} target="_blank" rel="noopener noreferrer" className="billing-file-link">
                      <span className={`billing-file-type billing-file-${item.fileType}`}>{item.fileType.toUpperCase()}</span>
                      {item.name}
                    </a>
                    {item.subPath && <div className="billing-muted billing-subpath">{item.subPath}</div>}
                  </td>
                  <td>{item.client}</td>
                  <td>{item.year || '-'}</td>
                  <td>
                    {formatDate(item.invoiceDate)}
                    {item.dateSource === 'modified' && (
                      <span className="billing-date-source" title="No date found in the file name; showing the SharePoint last-modified date">modified</span>
                    )}
                  </td>
                  <td>{formatBytes(item.size)}</td>
                  <td>
                    <button type="button" className="billing-icon-button" onClick={() => downloadSingle(item)} title="Download" aria-label={`Download ${item.name}`}>
                      <Download size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className="billing-pagination">
            <button type="button" className="btn btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button type="button" className="btn btn-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</button>
          </div>
        )}
      </div>

      {data.lastSyncedAt && (
        <p className="billing-muted billing-synced">Last synced {new Date(data.lastSyncedAt).toLocaleString()}</p>
      )}

      {emailOpen && (
        <EmailModal invoices={selectedInvoices} onClose={() => setEmailOpen(false)} onSend={sendEmail} sending={sending} />
      )}
      {indexAction && (
        <div className="billing-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="billing-index-action-title">
          <div className="billing-modal card">
            <h2 id="billing-index-action-title">{indexAction === 'clear' ? 'Clear local invoice index?' : 'Stop invoice indexing?'}</h2>
            <p>{indexAction === 'clear'
              ? 'This stops any active scan and removes all invoice metadata from this app for everyone. Your indexing account and folder selection are kept.'
              : 'This cancels queued or active indexing and discards unfinished scan metadata. The last completed invoice list is kept.'}</p>
            <p><strong>SharePoint files and folders will never be altered or removed by this action.</strong></p>
            <p>Daily indexing will remain paused until an authorized administrator selects Re-index now.</p>
            {indexActionError && <p className="error" role="alert">{indexActionError}</p>}
            <div className="billing-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setIndexAction(null)} disabled={indexActionPending}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={confirmIndexAction} disabled={indexActionPending}>
                {indexActionPending ? 'Working…' : indexAction === 'clear' ? 'Clear local metadata' : 'Stop and pause indexing'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Billing = () => (
  <ProtectedRoute requiredTab="billing">
    <BillingContent />
  </ProtectedRoute>
);

export default Billing;
